const experimentTest = {
  id: 'experiment',
  name: 'Experiment Analytics',
  subtitle: 'Mock interview · Data Engineer loop',
  description: 'A/B testing and experimentation platform analysis',
  pinnedNow: '2026-04-15 16:00:00',

  schema: `
    CREATE TABLE experiments (
      experiment_id TEXT PRIMARY KEY,
      experiment_name TEXT,
      started_at TEXT,
      ended_at TEXT,
      status TEXT,
      traffic_allocation REAL
    );

    CREATE TABLE variants (
      variant_id TEXT PRIMARY KEY,
      experiment_id TEXT,
      variant_name TEXT,
      variant_type TEXT,
      allocation_percent REAL,
      FOREIGN KEY (experiment_id) REFERENCES experiments(experiment_id)
    );

    CREATE TABLE user_assignments (
      assignment_id TEXT PRIMARY KEY,
      user_id TEXT,
      experiment_id TEXT,
      variant_id TEXT,
      assigned_at TEXT,
      FOREIGN KEY (experiment_id) REFERENCES experiments(experiment_id),
      FOREIGN KEY (variant_id) REFERENCES variants(variant_id)
    );

    CREATE TABLE user_events (
      event_id TEXT PRIMARY KEY,
      user_id TEXT,
      event_type TEXT,
      event_timestamp TEXT,
      revenue REAL
    );
  `,

  seedDataFn: (db) => {
    // Experiments
    const experiments = [
      ['exp1','checkout_flow_v2','2026-04-01 00:00','2026-04-14 23:59','completed',100.0],
      ['exp2','homepage_hero','2026-04-08 00:00','2026-04-22 23:59','running',50.0],
      ['exp3','pricing_page_test','2026-04-10 00:00',null,'running',100.0],
      ['exp4','search_ranking_algo','2026-03-15 00:00','2026-03-31 23:59','completed',100.0],
      ['exp5','notification_cadence','2026-04-05 00:00','2026-04-19 23:59','running',75.0]
    ];

    const expStmt = db.prepare(
      'INSERT INTO experiments (experiment_id, experiment_name, started_at, ended_at, status, traffic_allocation) VALUES (?, ?, ?, ?, ?, ?)'
    );
    experiments.forEach(e => expStmt.run(e));
    expStmt.free();

    // Variants
    const variants = [
      // exp1 (checkout_flow_v2) - 50/50 split
      ['v1a','exp1','control','control',50.0],
      ['v1b','exp1','one_page_checkout','treatment',50.0],

      // exp2 (homepage_hero) - 33/33/33 split (multi-variant)
      ['v2a','exp2','control','control',33.3],
      ['v2b','exp2','video_hero','treatment',33.3],
      ['v2c','exp2','carousel_hero','treatment',33.4],

      // exp3 (pricing_page_test) - 50/50 split
      ['v3a','exp3','control','control',50.0],
      ['v3b','exp3','annual_discount','treatment',50.0],

      // exp4 (search_ranking_algo) - 80/20 split (cautious rollout)
      ['v4a','exp4','control','control',80.0],
      ['v4b','exp4','ml_ranking','treatment',20.0],

      // exp5 (notification_cadence) - 50/50 split
      ['v5a','exp5','control','control',50.0],
      ['v5b','exp5','reduced_frequency','treatment',50.0]
    ];

    const variantStmt = db.prepare(
      'INSERT INTO variants (variant_id, experiment_id, variant_name, variant_type, allocation_percent) VALUES (?, ?, ?, ?, ?)'
    );
    variants.forEach(v => variantStmt.run(v));
    variantStmt.free();

    // User Assignments - Q1: Need to check for SRM (Sample Ratio Mismatch)
    // exp1 has perfect allocation, exp3 has SRM issue
    const assignments = [
      // exp1 - Perfect 50/50 allocation (1000 users each)
      ...Array.from({length: 1000}, (_, i) => [`a1-${i}`, `u${i+1}`, 'exp1', 'v1a', '2026-04-01 00:00']),
      ...Array.from({length: 1000}, (_, i) => [`a1-${i+1000}`, `u${i+1001}`, 'exp1', 'v1b', '2026-04-01 00:00']),

      // exp2 - Good 33/33/33 allocation (300 users each)
      ...Array.from({length: 300}, (_, i) => [`a2-${i}`, `u${i+3000}`, 'exp2', 'v2a', '2026-04-08 00:00']),
      ...Array.from({length: 300}, (_, i) => [`a2-${i+300}`, `u${i+3300}`, 'exp2', 'v2b', '2026-04-08 00:00']),
      ...Array.from({length: 300}, (_, i) => [`a2-${i+600}`, `u${i+3600}`, 'exp2', 'v2c', '2026-04-08 00:00']),

      // exp3 - SRM ISSUE: Should be 50/50 but is 60/40 (600 vs 400 users)
      ...Array.from({length: 600}, (_, i) => [`a3-${i}`, `u${i+5000}`, 'exp3', 'v3a', '2026-04-10 00:00']),
      ...Array.from({length: 400}, (_, i) => [`a3-${i+600}`, `u${i+5600}`, 'exp3', 'v3b', '2026-04-10 00:00']),

      // exp5 - Good 50/50 allocation (200 users each)
      ...Array.from({length: 200}, (_, i) => [`a5-${i}`, `u${i+7000}`, 'exp5', 'v5a', '2026-04-05 00:00']),
      ...Array.from({length: 200}, (_, i) => [`a5-${i+200}`, `u${i+7200}`, 'exp5', 'v5b', '2026-04-05 00:00'])
    ];

    const assignStmt = db.prepare(
      'INSERT INTO user_assignments (assignment_id, user_id, experiment_id, variant_id, assigned_at) VALUES (?, ?, ?, ?, ?)'
    );
    assignments.forEach(a => assignStmt.run(a));
    assignStmt.free();

    // User Events - Q2: Calculate conversion rates and Q3: Novelty effect
    const events = [];

    // exp1 events - Treatment has better conversion (12% vs 10%)
    // Control: 1000 users, 10% conversion = 100 purchases
    for (let i = 1; i <= 100; i++) {
      events.push([
        `e1c-${i}`,
        `u${i}`,
        'purchase',
        `2026-04-${String(Math.floor(Math.random() * 14) + 1).padStart(2, '0')} ${String(Math.floor(Math.random() * 24)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
        49.99 + Math.random() * 100
      ]);
    }

    // Treatment: 1000 users, 12% conversion = 120 purchases
    for (let i = 1; i <= 120; i++) {
      events.push([
        `e1t-${i}`,
        `u${i+1000}`,
        'purchase',
        `2026-04-${String(Math.floor(Math.random() * 14) + 1).padStart(2, '0')} ${String(Math.floor(Math.random() * 24)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
        49.99 + Math.random() * 100
      ]);
    }

    // exp2 events - Q3: Novelty effect analysis
    // Control: Week 1 (Apr 8-14) has 5% conv, no novelty
    const exp2ControlWeek1 = 15; // 5% of 300
    for (let i = 0; i < exp2ControlWeek1; i++) {
      events.push([
        `e2c1-${i}`,
        `u${3000 + i}`,
        'purchase',
        `2026-04-${String(8 + Math.floor(Math.random() * 7)).padStart(2, '0')} ${String(Math.floor(Math.random() * 24)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
        29.99
      ]);
    }

    // Treatment v2b: Week 1 (novelty) has 10% conv, Week 2 drops to 6%
    const exp2TreatmentWeek1 = 30; // 10% of 300
    for (let i = 0; i < exp2TreatmentWeek1; i++) {
      events.push([
        `e2t1-${i}`,
        `u${3300 + i}`,
        'purchase',
        `2026-04-${String(8 + Math.floor(Math.random() * 7)).padStart(2, '0')} ${String(Math.floor(Math.random() * 24)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
        29.99
      ]);
    }

    // Note: Week 2 would be Apr 15-21, but our pinned now is 2026-04-15 16:00,
    // so we add a few events in the 16 hours since week 2 started
    const exp2TreatmentWeek2Partial = 1; // Small number since only 16 hours elapsed
    for (let i = 0; i < exp2TreatmentWeek2Partial; i++) {
      events.push([
        `e2t2-${i}`,
        `u${3330 + i}`,
        'purchase',
        `2026-04-15 ${String(Math.floor(Math.random() * 16)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
        29.99
      ]);
    }

    // exp3 events - Control has 8% conv, Treatment has 11% (but SRM makes it suspicious)
    const exp3ControlConv = 48; // 8% of 600
    for (let i = 0; i < exp3ControlConv; i++) {
      events.push([
        `e3c-${i}`,
        `u${5000 + i}`,
        'purchase',
        `2026-04-${String(10 + Math.floor(Math.random() * 6)).padStart(2, '0')} ${String(Math.floor(Math.random() * 24)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
        79.99
      ]);
    }

    const exp3TreatmentConv = 44; // 11% of 400
    for (let i = 0; i < exp3TreatmentConv; i++) {
      events.push([
        `e3t-${i}`,
        `u${5600 + i}`,
        'purchase',
        `2026-04-${String(10 + Math.floor(Math.random() * 6)).padStart(2, '0')} ${String(Math.floor(Math.random() * 24)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
        79.99
      ]);
    }

    // Add some non-purchase events (views, clicks) for noise
    for (let i = 0; i < 500; i++) {
      events.push([
        `noise-${i}`,
        `u${Math.floor(Math.random() * 1000) + 1}`,
        Math.random() > 0.5 ? 'page_view' : 'click',
        `2026-04-${String(Math.floor(Math.random() * 15) + 1).padStart(2, '0')} ${String(Math.floor(Math.random() * 24)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
        null
      ]);
    }

    const eventStmt = db.prepare(
      'INSERT INTO user_events (event_id, user_id, event_type, event_timestamp, revenue) VALUES (?, ?, ?, ?, ?)'
    );
    events.forEach(e => eventStmt.run(e));
    eventStmt.free();
  },

  questions: [
    {
      id: 1,
      difficulty: 'Warm-up',
      timeLimit: 600, // 10 minutes
      question: `Detect Sample Ratio Mismatch (SRM) in running experiments. For each currently running experiment (status='running'), compare the actual user allocation to the expected allocation for each variant.

Calculate: experiment_id, variant_id, expected_percent (from variants table), actual_count (number of users assigned), actual_percent (rounded to 2 decimals), and has_srm (1 if absolute difference between actual and expected is > 2%, 0 otherwise).

Only include experiments with status='running'. Order by experiment_id, variant_id.

Note: SRM indicates potential randomization bugs that can invalidate experiment results.`,
      referenceSQL: `
        WITH running_experiments AS (
          SELECT experiment_id
          FROM experiments
          WHERE status = 'running'
        ),
        variant_counts AS (
          SELECT
            ua.experiment_id,
            ua.variant_id,
            COUNT(*) AS actual_count
          FROM user_assignments ua
          WHERE ua.experiment_id IN (SELECT experiment_id FROM running_experiments)
          GROUP BY ua.experiment_id, ua.variant_id
        ),
        total_by_experiment AS (
          SELECT
            experiment_id,
            SUM(actual_count) AS total_users
          FROM variant_counts
          GROUP BY experiment_id
        )
        SELECT
          v.experiment_id,
          v.variant_id,
          v.allocation_percent AS expected_percent,
          vc.actual_count,
          ROUND((CAST(vc.actual_count AS REAL) / t.total_users) * 100, 2) AS actual_percent,
          CASE
            WHEN ABS(v.allocation_percent - ((CAST(vc.actual_count AS REAL) / t.total_users) * 100)) > 2.0
            THEN 1
            ELSE 0
          END AS has_srm
        FROM variants v
        JOIN variant_counts vc ON vc.variant_id = v.variant_id
        JOIN total_by_experiment t ON t.experiment_id = v.experiment_id
        ORDER BY v.experiment_id, v.variant_id
      `,
      solutionSQL: `WITH running_experiments AS (
  SELECT experiment_id
  FROM experiments
  WHERE status = 'running'
),
variant_counts AS (
  SELECT
    ua.experiment_id,
    ua.variant_id,
    COUNT(*) AS actual_count
  FROM user_assignments ua
  WHERE ua.experiment_id IN (SELECT experiment_id FROM running_experiments)
  GROUP BY ua.experiment_id, ua.variant_id
),
total_by_experiment AS (
  SELECT
    experiment_id,
    SUM(actual_count) AS total_users
  FROM variant_counts
  GROUP BY experiment_id
)
SELECT
  v.experiment_id,
  v.variant_id,
  v.allocation_percent AS expected_percent,
  vc.actual_count,
  ROUND((CAST(vc.actual_count AS REAL) / t.total_users) * 100, 2) AS actual_percent,
  CASE
    WHEN ABS(v.allocation_percent - ((CAST(vc.actual_count AS REAL) / t.total_users) * 100)) > 2.0
    THEN 1
    ELSE 0
  END AS has_srm
FROM variants v
JOIN variant_counts vc ON vc.variant_id = v.variant_id
JOIN total_by_experiment t ON t.experiment_id = v.experiment_id
ORDER BY v.experiment_id, v.variant_id`,
      expectedColumns: ['experiment_id', 'variant_id', 'expected_percent', 'actual_count', 'actual_percent', 'has_srm']
    },
    {
      id: 2,
      difficulty: 'Medium',
      timeLimit: 1200, // 20 minutes
      question: `Calculate the primary metric (purchase conversion rate) for all completed experiments. For each variant, compute the number of unique users assigned, number of unique users who made a purchase, conversion rate, and average revenue per user (ARPU).

A purchase is defined as an event_type='purchase'. Only count purchases that occurred after the user was assigned to the experiment and before the experiment ended.

Return: experiment_id, variant_name, variant_type (control/treatment), users_assigned, users_converted, conversion_rate (as percentage, rounded to 2 decimals), arpu (rounded to 2 decimals). Order by experiment_id, variant_type DESC (control first).

Important: Handle users who were assigned but never had any events.`,
      referenceSQL: `
        WITH completed_experiments AS (
          SELECT experiment_id, started_at, ended_at
          FROM experiments
          WHERE status = 'completed'
        ),
        variant_users AS (
          SELECT
            ua.experiment_id,
            ua.variant_id,
            ua.user_id,
            ua.assigned_at
          FROM user_assignments ua
          WHERE ua.experiment_id IN (SELECT experiment_id FROM completed_experiments)
        ),
        user_purchases AS (
          SELECT
            vu.experiment_id,
            vu.variant_id,
            vu.user_id,
            MAX(CASE WHEN ue.event_type = 'purchase' THEN 1 ELSE 0 END) AS did_convert,
            COALESCE(SUM(CASE WHEN ue.event_type = 'purchase' THEN ue.revenue ELSE 0 END), 0) AS total_revenue
          FROM variant_users vu
          JOIN completed_experiments ce ON ce.experiment_id = vu.experiment_id
          LEFT JOIN user_events ue ON ue.user_id = vu.user_id
            AND ue.event_timestamp >= vu.assigned_at
            AND ue.event_timestamp <= ce.ended_at
          GROUP BY vu.experiment_id, vu.variant_id, vu.user_id
        ),
        variant_metrics AS (
          SELECT
            up.experiment_id,
            up.variant_id,
            COUNT(*) AS users_assigned,
            SUM(up.did_convert) AS users_converted,
            SUM(up.total_revenue) AS total_revenue
          FROM user_purchases up
          GROUP BY up.experiment_id, up.variant_id
        )
        SELECT
          vm.experiment_id,
          v.variant_name,
          v.variant_type,
          vm.users_assigned,
          vm.users_converted,
          ROUND((CAST(vm.users_converted AS REAL) / vm.users_assigned) * 100, 2) AS conversion_rate,
          ROUND(vm.total_revenue / vm.users_assigned, 2) AS arpu
        FROM variant_metrics vm
        JOIN variants v ON v.variant_id = vm.variant_id
        ORDER BY vm.experiment_id, v.variant_type DESC
      `,
      solutionSQL: `WITH completed_experiments AS (
  SELECT experiment_id, started_at, ended_at
  FROM experiments
  WHERE status = 'completed'
),
variant_users AS (
  SELECT
    ua.experiment_id,
    ua.variant_id,
    ua.user_id,
    ua.assigned_at
  FROM user_assignments ua
  WHERE ua.experiment_id IN (SELECT experiment_id FROM completed_experiments)
),
user_purchases AS (
  SELECT
    vu.experiment_id,
    vu.variant_id,
    vu.user_id,
    MAX(CASE WHEN ue.event_type = 'purchase' THEN 1 ELSE 0 END) AS did_convert,
    COALESCE(SUM(CASE WHEN ue.event_type = 'purchase' THEN ue.revenue ELSE 0 END), 0) AS total_revenue
  FROM variant_users vu
  JOIN completed_experiments ce ON ce.experiment_id = vu.experiment_id
  LEFT JOIN user_events ue ON ue.user_id = vu.user_id
    AND ue.event_timestamp >= vu.assigned_at
    AND ue.event_timestamp <= ce.ended_at
  GROUP BY vu.experiment_id, vu.variant_id, vu.user_id
),
variant_metrics AS (
  SELECT
    up.experiment_id,
    up.variant_id,
    COUNT(*) AS users_assigned,
    SUM(up.did_convert) AS users_converted,
    SUM(up.total_revenue) AS total_revenue
  FROM user_purchases up
  GROUP BY up.experiment_id, up.variant_id
)
SELECT
  vm.experiment_id,
  v.variant_name,
  v.variant_type,
  vm.users_assigned,
  vm.users_converted,
  ROUND((CAST(vm.users_converted AS REAL) / vm.users_assigned) * 100, 2) AS conversion_rate,
  ROUND(vm.total_revenue / vm.users_assigned, 2) AS arpu
FROM variant_metrics vm
JOIN variants v ON v.variant_id = vm.variant_id
ORDER BY vm.experiment_id, v.variant_type DESC`,
      expectedColumns: ['experiment_id', 'variant_name', 'variant_type', 'users_assigned', 'users_converted', 'conversion_rate', 'arpu']
    },
    {
      id: 3,
      difficulty: 'Hard',
      timeLimit: 1800, // 30 minutes
      question: `Detect the "novelty effect" in experiment exp2 by comparing week-over-week conversion rates. Split the experiment duration into weekly cohorts based on assignment date, then calculate conversion rate for each variant in each week.

For exp2 (started 2026-04-08), create two weekly cohorts:
- Week 1: Assigned 2026-04-08 to 2026-04-14 (inclusive)
- Week 2: Assigned 2026-04-15 onwards

For each variant and week, calculate: variant_name, week_number (1 or 2), users_assigned, conversion_rate (percentage, rounded to 2 decimals), and week_over_week_change (percentage point change from week 1 to week 2, NULL for week 1).

A large positive change in week 1 followed by a drop in week 2 for treatment (but not control) indicates novelty effect.

Return results for exp2 only. Order by variant_name, week_number.

Note: Only count purchases that occurred within 7 days of assignment (to ensure fair comparison across weeks).`,
      referenceSQL: `
        WITH weekly_cohorts AS (
          SELECT
            ua.user_id,
            ua.variant_id,
            ua.assigned_at,
            CASE
              WHEN DATE(ua.assigned_at) >= '2026-04-08' AND DATE(ua.assigned_at) <= '2026-04-14' THEN 1
              WHEN DATE(ua.assigned_at) >= '2026-04-15' THEN 2
            END AS week_number
          FROM user_assignments ua
          WHERE ua.experiment_id = 'exp2'
            AND DATE(ua.assigned_at) >= '2026-04-08'
        ),
        user_conversions AS (
          SELECT
            wc.user_id,
            wc.variant_id,
            wc.week_number,
            MAX(CASE
              WHEN ue.event_type = 'purchase'
                AND ue.event_timestamp >= wc.assigned_at
                AND ue.event_timestamp <= datetime(wc.assigned_at, '+7 days')
              THEN 1
              ELSE 0
            END) AS converted
          FROM weekly_cohorts wc
          LEFT JOIN user_events ue ON ue.user_id = wc.user_id
          GROUP BY wc.user_id, wc.variant_id, wc.week_number
        ),
        weekly_metrics AS (
          SELECT
            variant_id,
            week_number,
            COUNT(*) AS users_assigned,
            SUM(converted) AS users_converted,
            ROUND((CAST(SUM(converted) AS REAL) / COUNT(*)) * 100, 2) AS conversion_rate
          FROM user_conversions
          GROUP BY variant_id, week_number
        )
        SELECT
          v.variant_name,
          wm.week_number,
          wm.users_assigned,
          wm.conversion_rate,
          CASE
            WHEN wm.week_number = 1 THEN NULL
            ELSE ROUND(
              wm.conversion_rate - LAG(wm.conversion_rate) OVER (PARTITION BY wm.variant_id ORDER BY wm.week_number),
              2
            )
          END AS week_over_week_change
        FROM weekly_metrics wm
        JOIN variants v ON v.variant_id = wm.variant_id
        ORDER BY v.variant_name, wm.week_number
      `,
      solutionSQL: `WITH weekly_cohorts AS (
  SELECT
    ua.user_id,
    ua.variant_id,
    ua.assigned_at,
    CASE
      WHEN DATE(ua.assigned_at) >= '2026-04-08' AND DATE(ua.assigned_at) <= '2026-04-14' THEN 1
      WHEN DATE(ua.assigned_at) >= '2026-04-15' THEN 2
    END AS week_number
  FROM user_assignments ua
  WHERE ua.experiment_id = 'exp2'
    AND DATE(ua.assigned_at) >= '2026-04-08'
),
user_conversions AS (
  SELECT
    wc.user_id,
    wc.variant_id,
    wc.week_number,
    MAX(CASE
      WHEN ue.event_type = 'purchase'
        AND ue.event_timestamp >= wc.assigned_at
        AND ue.event_timestamp <= datetime(wc.assigned_at, '+7 days')
      THEN 1
      ELSE 0
    END) AS converted
  FROM weekly_cohorts wc
  LEFT JOIN user_events ue ON ue.user_id = wc.user_id
  GROUP BY wc.user_id, wc.variant_id, wc.week_number
),
weekly_metrics AS (
  SELECT
    variant_id,
    week_number,
    COUNT(*) AS users_assigned,
    SUM(converted) AS users_converted,
    ROUND((CAST(SUM(converted) AS REAL) / COUNT(*)) * 100, 2) AS conversion_rate
  FROM user_conversions
  GROUP BY variant_id, week_number
)
SELECT
  v.variant_name,
  wm.week_number,
  wm.users_assigned,
  wm.conversion_rate,
  CASE
    WHEN wm.week_number = 1 THEN NULL
    ELSE ROUND(
      wm.conversion_rate - LAG(wm.conversion_rate) OVER (PARTITION BY wm.variant_id ORDER BY wm.week_number),
      2
    )
  END AS week_over_week_change
FROM weekly_metrics wm
JOIN variants v ON v.variant_id = wm.variant_id
ORDER BY v.variant_name, wm.week_number`,
      expectedColumns: ['variant_name', 'week_number', 'users_assigned', 'conversion_rate', 'week_over_week_change']
    }
  ]
};

export default experimentTest;
